import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/button";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { BetterAuthError } from "@/lib/backend/utils/error";
import { useSessionProvider } from "@/lib/providers/session/use-session";
import { Routes } from "@/lib/router";
import { history as globalHistory } from "@/lib/router/history";
import { useAuthFlowStore } from "@/lib/store/auth-flow";
import { getCurrentAppSlug } from "@/utils/auth";
import { checkApiReachability } from "@/utils/check-api-reachability";
import { useIonicQueryParam } from "@/utils/hooks/use-ionic-query-param";

export function SignInForm() {
  const state = useAuthFlowStore();
  const { t } = useTranslation();
  const history = useHistory();
  const myAuthClient = useSessionProvider();
  const validator = useValidator();
  const [showPassword, setShowPassword] = useState(false);

  const callbackUrl = useIonicQueryParam("callbackUrl") || Routes.App();

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      email: state.email,
      password: state.password,
      rememberMe: state.rememberMe,
    },
    onSubmit: async ({ value }) => {
      let errorCode = "";
      let errorMessage = "";

      // Save form values to store before submission
      // This persists the values even if navigation occurs
      state.email = value.email;
      state.password = value.password;
      state.rememberMe = value.rememberMe;

      // Ensure the backend knows which app/organization this request belongs to
      // Send as header instead of cookie for Capacitor compatibility
      const appSlug = getCurrentAppSlug();

      await myAuthClient.signin(
        {
          email: value.email,
          password: value.password,
          rememberMe: value.rememberMe,
          callbackURL: callbackUrl,
        },
        {
          onSuccess: () => {
            history.push(callbackUrl);
          },
          onError: async (e) => {
            const be = BetterAuthError.safeParse(e);
            if (be.success) {
              const error = be.data;
              errorCode = error.code;
              errorMessage = error.message;

              if (errorCode || errorMessage) {
                toast.error(t(`error-codes:${errorCode}`) ?? errorMessage);
              }
            } else {
              const isReachable = await checkApiReachability();
              if (!isReachable) {
                toast.error(
                  t("error-codes:BACKEND_UNREACHABLE", {
                    url: String(import.meta.env.VITE_BACKEND_URL).split("//")[1],
                  }),
                );
              } else {
                toast.error(t("error-codes:UNKNOWN"));
              }
            }
          },
        },
      );
    },
  });

  return (
    <FormElement className="flex flex-col gap-6">
      <form.AppField
        name="email"
        validators={{
          onBlur: validator.required({ name: "email" }),
          onSubmit: validator.email({ schema: validator.required({ name: "email" }) }),
        }}
        children={(field) => <field.InputField type="email" required withPlaceholder />}
      />
      <form.AppField
        name="password"
        validators={{
          onBlur: validator.required({ name: "password" }),
          onSubmit: validator.required({ name: "password" }),
        }}
        children={(field) => (
          <div className="relative flex items-center">
            <field.InputField
              type={showPassword ? "text" : "password"}
              required
              withPlaceholder
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        )}
      />
      <form.Subscribe
        selector={(state) => [state.isSubmitting] as const}
        children={([isSubmitting]) => (
          <div className="flex flex-col gap-4">
            <AuthSubmitButton type="submit" isSubmitting={isSubmitting}>
              {t("page.auth.sign-in.steps.password.action.primary")}
            </AuthSubmitButton>
            <div className="flex flex-col items-center">
              {import.meta.env.VITE_ENABLE_REGISTRATION === "true" && (
                <Button
                  fill="clear"
                  className="text-sm text-brand-500"
                  onClick={() => globalHistory.push(Routes.AuthRegister())}
                >
                  Nog geen account? Registreer je hier
                </Button>
              )}

              {/* Use global history instead of routerLink to avoid IonRouterOutlet navigation issues */}
              <Button
                fill="clear"
                className="text-sm text-brand-500"
                onClick={() => globalHistory.push(Routes.AuthForgotPassword())}
              >
                {t("page.auth.sign-in.steps.password.action.secondary")}
              </Button>
            </div>
          </div>
        )}
      />
    </FormElement>
  );
}
